import {
  BulkAuditPageResponseSchema,
  KycDecidirInputSchema,
  KycListResponseSchema,
  KycSubmissaoDtoSchema,
  type BulkAuditPageResponse,
  type BulkAuditQuery,
  type KycDecidirInput,
  type KycListResponse,
  type KycStatus,
  type KycSubmissaoDto,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Phase 18 v1.4 — KYC-02. Admin review surface.
 *
 *   listKyc({ status, cursor, limit })  → GET  /admin/kyc?...
 *   getKyc(id)                           → GET  /admin/kyc/:id
 *   decidirKyc(id, { status, motivo? }) → POST /admin/kyc/:id/decidir
 *
 * Phase 41 v1.9 — ADMIN-AUDIT-01. Admin BulkAuditLog query surface.
 *
 *   audit.listBulk({ acao?, actorId?, eventoId?, fromDate?, toDate?, cursor?, limit? })
 *     → GET /admin/audit/bulk?... (paginated BulkAuditLog rows)
 *
 * AdminGuard rejects non-admin tokens server-side with 403 admin_required.
 * The frontend should gate the UI behind the `auth.isAdmin` signal to avoid
 * navigating non-admins into screens that 403; this api-client surface does
 * not embed that check.
 */
export interface ListKycOpts {
  status?: KycStatus;
  cursor?: string;
  limit?: number;
}

/**
 * Phase 41 v1.9 — ADMIN-AUDIT-01. BulkAuditLog query sub-namespace.
 *
 * Mounted at `apiClient.admin.audit.listBulk(...)` to keep the admin surface
 * tidy as more audit views land. Constructor receives the parent ApiClient
 * so it shares the same baseUrl / auth / 401-retry pipeline.
 */
export class AdminAuditEndpoints {
  constructor(private readonly client: ApiClient) {}

  listBulk(opts: Partial<BulkAuditQuery> = {}): Promise<BulkAuditPageResponse> {
    return this.client.get<BulkAuditPageResponse>('/admin/audit/bulk', {
      query: {
        ...(opts.acao ? { acao: opts.acao as string } : {}),
        ...(opts.actorId ? { actorId: opts.actorId } : {}),
        ...(opts.eventoId ? { eventoId: opts.eventoId } : {}),
        ...(opts.fromDate ? { fromDate: opts.fromDate } : {}),
        ...(opts.toDate ? { toDate: opts.toDate } : {}),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
        ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
      },
      responseSchema: BulkAuditPageResponseSchema,
    });
  }
}

export class AdminEndpoints {
  /** Phase 41 v1.9 — admin audit views (BulkAuditLog and future). */
  public readonly audit: AdminAuditEndpoints;

  constructor(private readonly client: ApiClient) {
    this.audit = new AdminAuditEndpoints(client);
  }

  listKyc(opts: ListKycOpts = {}): Promise<KycListResponse> {
    return this.client.get<KycListResponse>('/admin/kyc', {
      query: {
        ...(opts.status ? { status: opts.status } : {}),
        ...(opts.cursor ? { cursor: opts.cursor } : {}),
        ...(opts.limit !== undefined ? { limit: opts.limit } : {}),
      },
      responseSchema: KycListResponseSchema,
    });
  }

  getKyc(id: string): Promise<KycSubmissaoDto> {
    return this.client.get<KycSubmissaoDto>(`/admin/kyc/${id}`, {
      responseSchema: KycSubmissaoDtoSchema,
    });
  }

  decidirKyc(
    id: string,
    input: KycDecidirInput,
  ): Promise<{ id: string; status: KycStatus; motivo: string | null; decididoEm: string | null }> {
    // Parse client-side so a malformed input throws BEFORE the round-trip.
    KycDecidirInputSchema.parse(input);
    return this.client.post(`/admin/kyc/${id}/decidir`, input);
  }
}

export type { BulkAuditPageResponse, BulkAuditQuery };
